import { usePostByTenantNoteAndNoteIdChangeIscollateralforloanIsCollateralForLoanMutation } from 'api/rtk-api'
import {
  LabelValueRow,
  LinkButton,
  Modal,
  OperationButton,
  RadioFieldSet
} from 'components/core-v2'
import Icon from 'components/core-v2/atoms/Icon'
import ToastNotification from 'components/core-v2/atoms/toast-notification/toastNotification'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useUser } from 'utilities/hooks/useUser'

import { TENANT } from '../../../../../const'
import {
  getUserDisplayInfo,
  isCollateralUser
} from '../../../../../modules/tradebuilder-v2/util/user'
import classes from './change-collateral.module.scss'

export type ChangeCollateralProps = {
  noteId: string
  value: boolean
}

type ChangeCollateralButtonProps = {
  showModal: () => void
  value: string
}

type ChangeCollateralModalProps = ChangeCollateralProps & {
  isOpen: boolean
  onClose: () => void
}

const ChangeCollateralModal = ({ isOpen, noteId, onClose, value }: ChangeCollateralModalProps) => {
  const form = useForm({ defaultValues: { isCollateralForLoan: value ? 'yes' : 'no' } })
  const [showToast, setShowToast] = useState(false)
  const [mutate, { isLoading, isSuccess }] =
    usePostByTenantNoteAndNoteIdChangeIscollateralforloanIsCollateralForLoanMutation()

  useEffect(() => {
    if (isSuccess) {
      setShowToast(true)
      onClose()
    }
  }, [isSuccess, onClose])

  const onSubmit = data => {
    mutate({
      isCollateralForLoan: data.isCollateralForLoan === 'yes',
      noteId,
      tenant: TENANT
    })
  }

  return (
    <Modal.Wrapper
      aria-labelledby="collateral-modal-label"
      isOpen={isOpen}
      width="sm"
      onClose={onClose}
    >
      <Modal.Header id="collateral-modal-label" onClose={onClose}>
        <h2>Collateral for loans</h2>
      </Modal.Header>
      <Modal.Body className="mb-4">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <RadioFieldSet
              legend=""
              name="isCollateralForLoan"
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' }
              ]}
            />
            <div className={classes.changeButtons}>
              <OperationButton isLoading={isLoading} operationType="progress" type="submit">
                Save
              </OperationButton>
              <OperationButton
                isLoading={isLoading}
                operationType="neutral"
                type="button"
                onClick={onClose}
              >
                Cancel
              </OperationButton>
            </div>
          </form>
        </FormProvider>
        <ToastNotification
          description="Collateral for loan changed successfully"
          notificationType="success"
          open={showToast}
          title="Collateral for loan"
          onClose={() => setShowToast(false)}
        />
      </Modal.Body>
    </Modal.Wrapper>
  )
}

const ChangeCollateralButton = ({ showModal, value }: ChangeCollateralButtonProps) => (
  <div className={classes.changeLink}>
    <span>{value}</span>
    <Icon name="squareInput" size="default" />
    <LinkButton underline onClick={() => showModal()}>
      Change
    </LinkButton>
  </div>
)

export const ChangeCollateral = ({ noteId, value }: ChangeCollateralProps) => {
  const [showModal, setShowModal] = useState(false)
  const { data: user } = useUser()
  const { userRoles } = getUserDisplayInfo(user)
  const collateralForLoanText = value ? 'Yes' : 'No'

  return isCollateralUser(userRoles) ? (
    <>
      <LabelValueRow
        showBottomBorder
        label="Collateral for loans"
        value={
          <ChangeCollateralButton
            showModal={() => setShowModal(true)}
            value={collateralForLoanText}
          />
        }
      />
      <ChangeCollateralModal
        isOpen={showModal}
        noteId={noteId}
        value={value}
        onClose={() => setShowModal(false)}
      />
    </>
  ) : (
    <LabelValueRow showBottomBorder label="Collateral for loans" value={collateralForLoanText} />
  )
}
